import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from './lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Initialize Supabase auth state
  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      // Check if we're coming from OAuth callback
      const hashParams = window.location.hash.substring(1);
      if (hashParams) {
        const urlParams = new URLSearchParams(hashParams);
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        
        if (accessToken) {
          // We're in OAuth callback, process the token
          try {
            console.log('Processing OAuth callback...');
            
            // Set the session manually for Supabase
            const { data: { session }, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || urlParams.get('provider_refresh_token')
            });
            
            if (error) {
              console.error('Error setting session:', error);
              // Clear the hash and show error
              window.history.replaceState({}, document.title, window.location.pathname);
              setLoading(false);
              return;
            }
            
            // Clear the URL hash immediately after setting session
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // If session was set successfully, exchange with backend (backend enforces allowed domains + allowed emails)
            if (session) {
              try {
                console.log('🔄 Exchanging token with backend...');
                const response = await axios.post(`${API_BASE}/auth/supabase`, 
                  new URLSearchParams({ token: session.access_token }),
                  { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
                );
                const { access_token, user: backendUser } = response.data;
                if (access_token && backendUser) {
                  setUser({
                    ...backendUser,
                    picture: session.user.user_metadata?.avatar_url ?? backendUser.picture,
                    role: backendUser.role ?? session.user.app_metadata?.role ?? session.user.role,
                    must_change_password: backendUser.must_change_password === true,
                  });
                  setToken(access_token);
                  console.log('✅ Authentication successful! User:', backendUser);
                  setLoading(false);
                  return;
                }
                throw new Error('Invalid response from backend');
              } catch (error) {
                console.error('❌ Backend authentication failed:', error);
                await supabase.auth.signOut();
                const detail = error.response?.data?.detail || error.message;
                alert(detail);
                setLoading(false);
                return;
              }
            } else {
              console.error('❌ Session is null after setSession');
              setLoading(false);
            }
          } catch (error) {
            console.error('Error processing OAuth callback:', error);
            // Clear the hash on error
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        try {
          console.log('🔄 Exchanging Supabase token for backend JWT...');
          const response = await axios.post(`${API_BASE}/auth/supabase`, 
            new URLSearchParams({ token: session.access_token }),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
          );
          const { access_token, user: backendUser } = response.data;
          if (access_token && backendUser) {
            setUser({
              ...backendUser,
              picture: session.user.user_metadata?.avatar_url ?? backendUser.picture,
              role: backendUser.role ?? session.user.app_metadata?.role ?? session.user.role,
              must_change_password: backendUser.must_change_password === true,
            });
            setToken(access_token);
            console.log('✅ Authentication successful! User:', backendUser);
          } else {
            throw new Error('Invalid response from backend');
          }
        } catch (error) {
          console.error('❌ Backend authentication failed:', error);
          await supabase.auth.signOut();
          alert(error.response?.data?.detail || error.message);
        }
      }
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);
        
        if (event === 'SIGNED_IN' && session) {
          try {
            console.log('Exchanging token with backend...');
            const response = await axios.post(`${API_BASE}/auth/supabase`, 
              new URLSearchParams({ token: session.access_token }),
              { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );
            const { access_token, user: backendUser } = response.data;
            setUser({
              ...backendUser,
              picture: session.user.user_metadata?.avatar_url ?? backendUser.picture,
              role: backendUser.role ?? session.user.app_metadata?.role ?? session.user.role,
              must_change_password: backendUser.must_change_password === true,
            });
            setToken(access_token);
            console.log('✅ Backend authentication successful!');
            try {
              await axios.post(`${API_BASE}/projects/cache/warm`, {}, {
                headers: { Authorization: `Bearer ${access_token}` }
              });
            } catch (cacheError) {
              console.log('⚠️ Cache warming failed (non-critical):', cacheError.message);
            }
            if (window.location.hash) {
              window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
            }
          } catch (error) {
            console.error('❌ Backend authentication failed:', error);
            await supabase.auth.signOut();
            alert(error.response?.data?.detail || error.message);
          }
          setAuthLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setToken(null);
          setAuthLoading(false);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [API_BASE]);

  const loginWithGoogle = async () => {
    setAuthLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });

      if (error) {
        console.error('Google login failed:', error);
        setAuthLoading(false);
        return { 
          success: false, 
          error: error.message || 'Authentication failed' 
        };
      }

      // The token exchange will happen in the auth state change listener
      return { success: true };
    } catch (error) {
      console.error('Google login failed:', error);
      setAuthLoading(false);
      return { 
        success: false, 
        error: error.message || 'Authentication failed' 
      };
    }
  };

  const loginWithEmailPassword = async (email, password) => {
    setAuthLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthLoading(false);
        return { success: false, error: error.message || 'Invalid email or password' };
      }
      if (!data?.session) {
        setAuthLoading(false);
        return { success: false, error: 'No session returned' };
      }
      const response = await axios.post(`${API_BASE}/auth/supabase`,
        new URLSearchParams({ token: data.session.access_token }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      const { access_token, user: backendUser } = response.data;
      if (access_token && backendUser) {
        setUser({
          ...backendUser,
          picture: data.session.user?.user_metadata?.avatar_url ?? backendUser.picture,
          role: backendUser.role ?? data.session.user?.app_metadata?.role,
          must_change_password: backendUser.must_change_password === true,
        });
        setToken(access_token);
        setAuthLoading(false);
        return { success: true };
      }
      setAuthLoading(false);
      return { success: false, error: 'Invalid response from server' };
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Login failed';
      setAuthLoading(false);
      return { success: false, error: detail };
    }
  };

  const clearMustChangePassword = async () => {
    try {
      await axios.post(`${API_BASE}/auth/clear-must-change-password`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(prev => prev ? { ...prev, must_change_password: false } : null);
    } catch (err) {
      console.error('Failed to clear must_change_password:', err);
      throw err;
    }
  };

  const logout = async () => {
    setLogoutLoading(true);
    try {
      await supabase.auth.signOut({ scope: 'local' });
      setUser(null);
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
      // Clear any Supabase auth keys from storage so refresh doesn't restore session
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('sb-')) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch (_) {}
    } finally {
      setLogoutLoading(false);
    }
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isCompanyEmail = () => {
    if (!user) return false;
    
    // Only CultureFlipper domain is allowed
    const allowedDomains = ['cultureflipper.com'];
    const domain = user.email.split('@')[1];
    return allowedDomains.includes(domain);
  };

  const value = {
    user,
    token,
    setUser,
    setToken,
    loading,
    authLoading,
    logoutLoading,
    loginWithGoogle,
    loginWithEmailPassword,
    clearMustChangePassword,
    logout,
    isAuthenticated,
    isCompanyEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};